import {MilestoneIcon} from '@primer/octicons-react'
import type React from 'react'
import {useCallback, useEffect, useMemo, useState} from 'react'
import {graphql, readInlineData, usePaginationFragment, useRelayEnvironment} from 'react-relay'

import {VALUES} from '../constants/values'
import {HOTKEYS} from '../constants/hotkeys'
import {LABELS} from '../constants/labels'
import type {MilestoneListPaginationQuery} from './__generated__/MilestoneListPaginationQuery.graphql'
import type {
  MilestonePickerMilestone$data,
  MilestonePickerMilestone$key,
} from './__generated__/MilestonePickerMilestone.graphql'
import type {MilestonePickerPaginated$key} from './__generated__/MilestonePickerPaginated.graphql'
import type {MilestonePickerQuery} from './__generated__/MilestonePickerQuery.graphql'
import {type ExtendedItemProps, ItemPicker, type SharedBulkActionsItemPickerProps} from './ItemPicker'
import type {ItemGroup} from '../shared'
import {SharedPicker} from './SharedPicker'
import {commitUpdateIssueMilestoneBulkByQueryMutation} from '../mutations/update-issue-milestone-bulk-by-query-mutation'
import {commitUpdateIssueMilestoneBulkMutation} from '../mutations/update-issue-milestone-bulk-mutation'
import type {updateIssueMilestoneBulkMutation$data} from '../mutations/__generated__/updateIssueMilestoneBulkMutation.graphql'
import type {updateIssueMilestoneBulkByQueryMutation$data} from '../mutations/__generated__/updateIssueMilestoneBulkByQueryMutation.graphql'
import {useItemPickerErrorFallback} from './useItemPickerErrorFallback'
import {clientSideRelayFetchQueryRetained} from '@github-ui/relay-environment'
import {IS_SERVER} from '@github-ui/ssr-utils'
import {useKeyPress} from '@github-ui/use-key-press'

//
// ItemPicker <== ItemPickerWrapper (internal) <== ItemPickerFetcher (internal) <== MilestonePicker (exported)
//                                                                              <== BulkMilestonePicker (exported)

export type Milestone = MilestonePickerMilestone$data

export type MilestonePickerProps = {
  repo: string
  owner: string
  activeMilestone: Milestone | null
  readonly?: boolean
  insidePortal?: boolean
  shortcutEnabled: boolean
  anchorElement: (props: React.HTMLAttributes<HTMLElement>) => JSX.Element
  /**
   * Whether to render the milestone picker as a nested select panel (true) versus a standalone select
   * panel (false; default).
   */
  nested?: boolean
  triggerOpen?: boolean
  initialFilter?: string
  shortcutHintVisible?: boolean
  isListMilestoneFilter?: boolean
  onSelectionChanged: (selectedItems: Milestone[]) => void
}

type ItemPickerWrapperProps = MilestonePickerProps & {
  paginatedKey: MilestonePickerPaginated$key | null
  isLoading: boolean
}

const openGroup: ItemGroup = {groupId: 'open', header: {title: 'Open', variant: 'filled'}}
const closedGroup: ItemGroup = {groupId: 'closed', header: {title: 'Closed', variant: 'filled'}}

export const MilestonesPickerGraphqlQuery = graphql`
  query MilestonePickerQuery($owner: String!, $repo: String!) {
    repository(owner: $owner, name: $repo) {
      ...MilestonePickerPaginated
    }
  }
`

export const MilestonePickerPaginated = graphql`
  fragment MilestonePickerPaginated on Repository
  @argumentDefinitions(cursor: {type: "String"}, count: {type: "Int", defaultValue: 100})
  @refetchable(queryName: "MilestoneListPaginationQuery") {
    milestones(first: $count, after: $cursor) @connection(key: "Repository_milestones") {
      edges {
        node {
          ...MilestonePickerMilestone
        }
      }
      totalCount
    }
  }
`

export const MilestoneFragment = graphql`
  fragment MilestonePickerMilestone on Milestone @inline {
    id
    title
    closed
    # eslint-disable-next-line relay/unused-fields
    dueOn
    # eslint-disable-next-line relay/unused-fields
    progressPercentage
    # eslint-disable-next-line relay/unused-fields
    url
  }
`

function ItemPickerFetcher({repo, owner, ...rest}: MilestonePickerProps) {
  const environment = useRelayEnvironment()
  const [isLoading, setIsLoading] = useState(true)
  const [fetchKey, setFetchKey] = useState(0)
  const [isError, setIsError] = useState(false)
  const [data, setData] = useState<MilestonePickerPaginated$key | null>(null)

  useEffect(() => {
    if (!IS_SERVER) {
      clientSideRelayFetchQueryRetained<MilestonePickerQuery>({
        environment,
        query: MilestonesPickerGraphqlQuery,
        variables: {owner, repo},
      }).subscribe({
        next: internalData => {
          const labels = internalData.repository
          setData(labels ?? null)
          setIsLoading(false)
          setIsError(false)
        },
        error: () => {
          setIsError(true)
          setIsLoading(false)
        },
      })
    }
  }, [environment, fetchKey, owner, repo])

  const {createFallbackComponent} = useItemPickerErrorFallback({
    errorMessage: LABELS.cantEditItems('milestones'),
    anchorElement: rest.anchorElement,
    open: true,
  })

  if (isError) {
    return createFallbackComponent(() => setFetchKey(current => current + 1))
  }

  return <ItemPickerWrapper repo={repo} owner={owner} paginatedKey={data} isLoading={isLoading} {...rest} />
}

function ItemPickerWrapper({
  paginatedKey,
  activeMilestone,
  onSelectionChanged,
  insidePortal,
  shortcutEnabled,
  anchorElement,
  nested,
  triggerOpen,
  initialFilter,
  isLoading,
  isListMilestoneFilter,
}: ItemPickerWrapperProps) {
  const [filter, setFilter] = useState('')

  const {data, loadNext, isLoadingNext, hasNext} = usePaginationFragment<
    MilestoneListPaginationQuery,
    MilestonePickerPaginated$key
  >(MilestonePickerPaginated, paginatedKey)

  const totalNumberOfMilestones = data?.milestones?.totalCount || 0
  const fetchedMilestones = useMemo(
    () =>
      data?.milestones?.edges?.flatMap(a =>
        // eslint-disable-next-line no-restricted-syntax
        a?.node ? [readInlineData<MilestonePickerMilestone$key>(MilestoneFragment, a.node)] : [],
      ) || [],
    [data],
  )

  useEffect(() => {
    if (hasNext && !isLoadingNext && fetchedMilestones.length < VALUES.milestonesMaxPreloadCount) {
      loadNext(VALUES.milestonesPageSize)
    }
  }, [loadNext, fetchedMilestones, totalNumberOfMilestones, hasNext, isLoadingNext])

  const items = useMemo(() => {
    const repositoryMilestones = fetchedMilestones.slice().sort((a, b) => a.title.localeCompare(b.title))
    isListMilestoneFilter && repositoryMilestones.unshift(LABELS.noMilestoneData as MilestonePickerMilestone$data)
    if (!filter) return repositoryMilestones
    return repositoryMilestones.filter(l => l.title.toLowerCase().indexOf(filter.toLowerCase()) >= 0)
  }, [fetchedMilestones, filter, isListMilestoneFilter])

  const openItems = useMemo(() => {
    if (isListMilestoneFilter) {
      return items.filter(m => m.closed === false)
    } else {
      return []
    }
  }, [isListMilestoneFilter, items])

  const filterItems = useCallback((value: string) => {
    setFilter(value)
  }, [])

  const getItemKey = useCallback((milestone: Milestone) => milestone.id, [])

  const convertToItemProps = useCallback((milestone: Milestone): ExtendedItemProps<Milestone> => {
    return {
      id: milestone.id,
      text: milestone.title,
      sx: {wordBreak: 'break-word'},
      source: milestone,
      groupId: milestone.closed ? closedGroup.groupId : openGroup.groupId,
      leadingVisual: () => <MilestoneIcon size={16} />,
    }
  }, [])

  // Only show groups for which the filter returns at least one item
  const groups: ItemGroup[] = useMemo(() => {
    const itemGroups = []
    if (items.some(i => !i.closed)) {
      itemGroups.push(openGroup)
    }
    if (items.some(i => i.closed)) {
      itemGroups.push(closedGroup)
    }
    return itemGroups
  }, [items])

  return (
    <ItemPicker
      loading={isLoading}
      items={isListMilestoneFilter ? openItems : items}
      initialSelectedItems={activeMilestone ? [activeMilestone] : []}
      openHotkey={shortcutEnabled ? HOTKEYS.milestonePicker : undefined}
      filterItems={filterItems}
      title={LABELS.milestonesHeader}
      getItemKey={getItemKey}
      convertToItemProps={convertToItemProps}
      placeholderText={`Filter milestones ${
        fetchedMilestones.length >= VALUES.milestonesMaxPreloadCount
          ? `(${VALUES.milestonesMaxPreloadCount} milestones loaded)`
          : ''
      }`}
      selectionVariant="single"
      onSelectionChange={onSelectionChanged}
      groups={isListMilestoneFilter ? undefined : groups}
      renderAnchor={anchorProps => anchorElement(anchorProps)}
      insidePortal={insidePortal}
      height={'large'}
      width={'medium'}
      nested={nested}
      resultListAriaLabel={'Milestone results'}
      triggerOpen={triggerOpen}
      initialFilter={initialFilter}
    />
  )
}

type BulkIssueMilestonePickerProps = Omit<MilestonePickerProps, 'issueId' | 'onIssueUpdate' | 'onSelectionChanged'> &
  SharedBulkActionsItemPickerProps

export function BulkIssueMilestonePicker({
  onError,
  onCompleted,
  issuesToActOn,
  useQueryForAction,
  repositoryId,
  query,
  ...rest
}: BulkIssueMilestonePickerProps) {
  const environment = useRelayEnvironment()
  const onSelectionChange = useCallback(
    (selectedMilestone: Milestone[]) => {
      const milestoneId = selectedMilestone.length === 1 ? selectedMilestone[0]?.id : null
      if (!milestoneId) return
      const mutationArgs = {
        environment,
        onError: (error: Error) => {
          onError?.(error)
        },
      }

      if (useQueryForAction && repositoryId && query) {
        commitUpdateIssueMilestoneBulkByQueryMutation({
          ...mutationArgs,
          input: {
            repositoryId,
            query,
            milestoneId,
          },
          onCompleted: ({updateIssuesBulkByQuery}: updateIssueMilestoneBulkByQueryMutation$data) => {
            onCompleted?.(updateIssuesBulkByQuery?.jobId || undefined)
          },
        })
      } else {
        commitUpdateIssueMilestoneBulkMutation({
          ...mutationArgs,
          input: {
            ids: [...issuesToActOn],
            milestoneId,
          },
          onCompleted: ({updateIssuesBulk}: updateIssueMilestoneBulkMutation$data) => {
            onCompleted?.(updateIssuesBulk?.jobId || undefined)
          },
        })
      }
    },
    [environment, issuesToActOn, onCompleted, onError, query, repositoryId, useQueryForAction],
  )

  return <MilestonePicker onSelectionChanged={onSelectionChange} {...rest} />
}

export function MilestonePicker({
  repo,
  owner,
  shortcutEnabled,
  anchorElement,
  readonly,
  activeMilestone,
  isListMilestoneFilter,
  ...rest
}: MilestonePickerProps) {
  const [wasTriggered, setWasTriggered] = useState(false)

  useKeyPress(shortcutEnabled ? [HOTKEYS.milestonePicker] : [], (e: KeyboardEvent) => {
    if (!wasTriggered) {
      setWasTriggered(true)
      e.preventDefault()
    }
  })

  if (!wasTriggered) {
    return anchorElement({onClick: () => setWasTriggered(true)})
  }

  return (
    <ItemPickerFetcher
      repo={repo}
      owner={owner}
      readonly={readonly}
      shortcutEnabled={shortcutEnabled}
      initialFilter={''}
      activeMilestone={activeMilestone}
      triggerOpen={true}
      anchorElement={anchorElement}
      isListMilestoneFilter={isListMilestoneFilter}
      {...rest}
    />
  )
}

export function DefaultMilestoneAnchor({
  activeMilestone,
  readonly,
  nested,
  shortcutEnabled,
  shortcutHintVisible,
  anchorProps,
}: Pick<MilestonePickerProps, 'activeMilestone' | 'readonly' | 'nested' | 'shortcutEnabled' | 'shortcutHintVisible'> & {
  anchorProps?: React.HTMLAttributes<HTMLElement> | undefined
}) {
  return (
    <SharedPicker
      anchorText={LABELS.noMilestones}
      sharedPickerMainValue={activeMilestone?.title}
      anchorProps={readonly ? undefined : anchorProps}
      ariaLabel={LABELS.selectMilestones}
      readonly={readonly}
      nested={nested}
      leadingIcon={MilestoneIcon}
      hotKey={!readonly && shortcutEnabled && shortcutHintVisible ? HOTKEYS.milestonePicker.toUpperCase() : undefined}
    />
  )
}

try{ ItemPickerFetcher.displayName ||= 'ItemPickerFetcher' } catch {}
try{ ItemPickerWrapper.displayName ||= 'ItemPickerWrapper' } catch {}
try{ BulkIssueMilestonePicker.displayName ||= 'BulkIssueMilestonePicker' } catch {}
try{ MilestonePicker.displayName ||= 'MilestonePicker' } catch {}
try{ DefaultMilestoneAnchor.displayName ||= 'DefaultMilestoneAnchor' } catch {}